import React from 'react';
import { Box, Typography } from '@mui/material';
import GroupIcon from "@mui/icons-material/Group";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

interface LeadStats {
  totalLeadsCount: number;
  gettingReadyLeads: number;
  inProgressLeadsCount: number;
  finishedLeadsCount: number;
  failedLeadsCount: number;
  blackListedCount: number;
  pausedCount: number;
  waitingUntilCount: number;
}

interface StatusCardsProps {
  leadStats: LeadStats | null;
}

const StatusCards: React.FC<StatusCardsProps> = ({ leadStats }) => {
  const statusIconMap: Record<string, JSX.Element> = {
    "All Leads": <svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.6663 13V9M10.6663 11H14.6663M7.99967 9H5.33301C4.0905 9 3.46924 9 2.97919 9.20299C2.32578 9.47364 1.80665 9.99277 1.536 10.6462C1.33301 11.1362 1.33301 11.7575 1.33301 13M10.333 1.19384C11.3103 1.58943 11.9997 2.54754 11.9997 3.66667C11.9997 4.78579 11.3103 5.7439 10.333 6.13949M8.99967 3.66667C8.99967 5.13943 7.80577 6.33333 6.33301 6.33333C4.86025 6.33333 3.66634 5.13943 3.66634 3.66667C3.66634 2.19391 4.86025 1 6.33301 1C7.80577 1 8.99967 2.19391 8.99967 3.66667Z" stroke="#325CD6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
    ,
    "In progress": <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7.99967 3.99967V7.99967L10.6663 9.33301M14.6663 7.99967C14.6663 11.6816 11.6816 14.6663 7.99967 14.6663C4.31778 14.6663 1.33301 11.6816 1.33301 7.99967C1.33301 4.31778 4.31778 1.33301 7.99967 1.33301C11.6816 1.33301 14.6663 4.31778 14.6663 7.99967Z" stroke="#325CD6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
    ,
    "Completed": <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.99967 9.99967L5.33301 11.333L8.33301 8.33301M5.33301 5.33301V3.46634C5.33301 2.7196 5.33301 2.34624 5.47833 2.06102C5.60616 1.81014 5.81014 1.60616 6.06102 1.47833C6.34624 1.33301 6.7196 1.33301 7.46634 1.33301H12.533C13.2797 1.33301 13.6531 1.33301 13.9383 1.47833C14.1892 1.60616 14.3932 1.81014 14.521 2.06102C14.6663 2.34624 14.6663 2.7196 14.6663 3.46634V8.53301C14.6663 9.27974 14.6663 9.65311 14.521 9.93833C14.3932 10.1892 14.1892 10.3932 13.9383 10.521C13.6531 10.6663 13.2797 10.6663 12.533 10.6663H10.6663M3.46634 14.6663H8.53301C9.27974 14.6663 9.65311 14.6663 9.93833 14.521C10.1892 14.3932 10.3932 14.1892 10.521 13.9383C10.6663 13.6531 10.6663 13.2797 10.6663 12.533V7.46634C10.6663 6.7196 10.6663 6.34624 10.521 6.06102C10.3932 5.81014 10.1892 5.60616 9.93833 5.47833C9.65311 5.33301 9.27974 5.33301 8.53301 5.33301H3.46634C2.7196 5.33301 2.34624 5.33301 2.06102 5.47833C1.81014 5.60616 1.60616 5.81014 1.47833 6.06102C1.33301 6.34624 1.33301 6.7196 1.33301 7.46634V12.533C1.33301 13.2797 1.33301 13.6531 1.47833 13.9383C1.60616 14.1892 1.81014 14.3932 2.06102 14.521C2.34624 14.6663 2.7196 14.6663 3.46634 14.6663Z" stroke="#325CD6" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
    ,
    "Awaiting": <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7.99967 1.33301V3.99967M7.99967 11.9997V14.6663M3.99967 7.99967H1.33301M14.6663 7.99967H11.9997M12.7186 12.7186L10.833 10.833M12.7186 3.33297L10.833 5.21859M3.28072 12.7186L5.16634 10.833M3.28072 3.33297L5.16634 5.21859" stroke="#325CD6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
    ,
    "Paused": <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5.33333 9V5M8.66667 9V5M4.2 13H9.8C10.9201 13 11.4802 13 11.908 12.782C12.2843 12.5903 12.5903 12.2843 12.782 11.908C13 11.4802 13 10.9201 13 9.8V4.2C13 3.0799 13 2.51984 12.782 2.09202C12.5903 1.71569 12.2843 1.40973 11.908 1.21799C11.4802 1 10.9201 1 9.8 1H4.2C3.0799 1 2.51984 1 2.09202 1.21799C1.71569 1.40973 1.40973 1.71569 1.21799 2.09202C1 2.51984 1 3.0799 1 4.2V9.8C1 10.9201 1 11.4802 1.21799 11.908C1.40973 12.2843 1.71569 12.5903 2.09202 12.782C2.51984 13 3.0799 13 4.2 13Z" stroke="#325CD6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
    ,
    "Blacklisted": <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7.99951 6.00015V8.66682M7.99951 11.3335H8.00618M7.07639 2.59464L1.59313 12.0657C1.289 12.591 1.13693 12.8537 1.1594 13.0693C1.17901 13.2573 1.27752 13.4282 1.43043 13.5394C1.60573 13.6668 1.90924 13.6668 2.51625 13.6668H13.4828C14.0898 13.6668 14.3933 13.6668 14.5686 13.5394C14.7215 13.4282 14.82 13.2573 14.8396 13.0693C14.8621 12.8537 14.71 12.591 14.4059 12.0657L8.92263 2.59463C8.61959 2.07119 8.46806 1.80947 8.27038 1.72157C8.09794 1.64489 7.90108 1.64489 7.72865 1.72157C7.53096 1.80947 7.37944 2.07119 7.07639 2.59464Z" stroke="#325CD6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
    ,
  };

  const leadStatusMap = [
    { label: "All Leads", key: "totalLeadsCount" },
    { label: "In progress", key: "inProgressLeadsCount" },
    { label: "Completed", key: "finishedLeadsCount" },
    { label: "Awaiting", key: "waitingUntilCount" },
    { label: "Paused", key: "pausedCount" },
    { label: "Blacklisted", key: "blackListedCount" },
  ];

  return (
    <Box sx={{ display: "flex", gap: 4, px: 2, py: 3 }}>
      {leadStatusMap.map(({ label, key }, index) => (
        <Box
          key={index}
          sx={{
            width: "15%",
            minWidth: 140,
            textAlign: "left",
            border: "1px solid #E5EAF2",
            borderRadius: "12px",
            py: 2,
            px: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
            justifyContent: "left",
            cursor: "pointer",
          }}
        >
          <Box
            sx={{
              bgcolor: "#325CD61A",
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1.5,
            }}
          >
            {statusIconMap[label]}
          </Box>
          <Typography
            variant="body2"
            fontWeight={500}
            color="#1A1A1A"
          >
            {label}
          </Typography>
          <Typography
            variant="h6"
            fontWeight={700}
            color="#1A1A1A"
            mt={0.5}
          >
            {leadStats
              ? leadStats[key as keyof typeof leadStats] ?? 0
              : 0}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default StatusCards; 